import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { displayError } from '../../helpers';
import { getSiteDocuments } from '../../services/CustomerSiteServices';
import { fetchPDF } from '../../services/QuotationServices';
import { printSalesOrder } from '../../services/SalesOrderServices';
import { printWorkScheduleReport } from '../../services/WorkScheduleServices';
import { printDesignMix } from '../../services/DesignMixServices';
import { printDeliveryChallan } from '../../services/DeliveryChallanServices';
import { printSalesInvoice } from '../../services/SalesInvoiceServices';
import { openPdfResponse } from '../../utils/openPdfInApp';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import ReportExportDropdown from '../../components/ReportExportDropdown';

function documentMatchesSearch(doc, query, isDeliveryChallan) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const fields = isDeliveryChallan
    ? [
        doc.project_name,
        doc.so_no,
        doc.ws_no,
        doc.doc_no,
        doc.label,
        doc.doc_date,
        doc.doc_time,
        doc.grade,
        doc.qty,
        doc.rate,
        doc.amount,
        doc.vehicle_no,
        doc.driver,
        doc.total_km,
        doc.total_time,
      ]
    : [doc.doc_no, doc.label, doc.doc_date, doc.extra];
  return fields.some((value) => String(value ?? '').toLowerCase().includes(q));
}

export const SITE_DOC_LABELS = {
  quotation: 'Quotation',
  sales_order: 'Sales Order',
  schedule: 'Schedules',
  design_mix: 'Design Mix',
  delivery_challan: 'Delivery Challan',
  invoice: 'Invoice',
};

function formatDocDate(value) {
  if (!value) return '-';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD-MM-YYYY') : String(value);
}

function formatNumber(value, digits = 2) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return Number.isInteger(num) ? String(num) : num.toFixed(digits);
}

async function downloadSiteDocument(cookies, docType, docId, label) {
  let response;
  let title = SITE_DOC_LABELS[docType] || 'Document';
  let fileName = `${docType}_${docId}.pdf`;

  switch (docType) {
    case 'quotation':
      response = await fetchPDF(cookies, docId);
      title = `Quotation ${label || docId}`;
      fileName = `quotation_${label || docId}.pdf`;
      break;
    case 'sales_order':
      response = await printSalesOrder(cookies, docId);
      title = `Sales Order ${label || docId}`;
      fileName = `sales_order_${label || docId}.pdf`;
      break;
    case 'schedule':
      response = await printWorkScheduleReport(cookies, docId);
      title = `Schedule ${label || docId}`;
      fileName = `schedule_${label || docId}.pdf`;
      break;
    case 'design_mix':
      response = await printDesignMix(cookies, docId);
      title = `Design Mix ${label || docId}`;
      fileName = `design_mix_${label || docId}.pdf`;
      break;
    case 'delivery_challan':
      response = await printDeliveryChallan(cookies, docId);
      title = `Delivery Challan ${label || docId}`;
      fileName = `delivery_challan_${label || docId}.pdf`;
      break;
    case 'invoice':
      response = await printSalesInvoice(cookies, docId);
      title = `Invoice ${label || docId}`;
      fileName = `invoice_${label || docId}.pdf`;
      break;
    default:
      throw new Error('Unsupported document type');
  }

  openPdfResponse(response, {
    title,
    downloadFileName: fileName.replace(/[^\w.\-]+/g, '_'),
  });
}

function buildSiteDocumentsExportRows(documents, isDeliveryChallan) {
  if (isDeliveryChallan) {
    return documents.map((doc, index) => ({
      '#': index + 1,
      'Project Name': doc.project_name || '',
      'SO No': doc.so_no || '',
      'WS No': doc.ws_no || '',
      'DC No': doc.doc_no || doc.label || doc.id || '',
      Date: formatDocDate(doc.doc_date),
      Time: doc.doc_time || '',
      Grade: doc.grade || '',
      Qty: doc.qty ?? '',
      Rate: doc.rate ?? '',
      Amount: doc.amount ?? '',
      'Vehicle No': doc.vehicle_no || '',
      Driver: doc.driver || '',
      'Total KM': doc.total_km ?? '',
      'Total Time': doc.total_time ?? '',
    }));
  }
  return documents.map((doc, index) => ({
    '#': index + 1,
    'Document No': doc.doc_no || doc.label || doc.id || '',
    Date: formatDocDate(doc.doc_date),
    Details: doc.extra || '',
  }));
}

function SiteDocumentsModal({
  open,
  onClose,
  cookies,
  siteId,
  siteName,
  docType,
}) {
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [searchText, setSearchText] = useState('');

  const loadDocuments = useCallback(() => {
    if (!open || !siteId || !docType) return;
    setLoading(true);
    setDocuments([]);
    setSearchText('');
    getSiteDocuments(cookies, siteId, docType)
      .then((response) => {
        setDocuments(response.documents || []);
      })
      .catch((error) => {
        displayError(error?.response?.data ?? error.message, 'Failed to load documents');
        setDocuments([]);
      })
      .finally(() => setLoading(false));
  }, [cookies, docType, open, siteId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (!open) {
      setSearchText('');
    }
  }, [open]);

  const isDeliveryChallan = docType === 'delivery_challan';

  const filteredDocuments = useMemo(
    () => documents.filter((doc) => documentMatchesSearch(doc, searchText, isDeliveryChallan)),
    [documents, searchText, isDeliveryChallan],
  );

  const exportPayload = useMemo(() => {
    const rows = buildSiteDocumentsExportRows(filteredDocuments, isDeliveryChallan);
    const columns = rows.length
      ? Object.keys(rows[0]).map((header) => ({
          headerName: header,
          fieldName: header,
        }))
      : [];
    const safeSite = String(siteName || 'Site').replace(/[^\w.\-]+/g, '_');
    const safeType = String(docType || 'documents').replace(/[^\w.\-]+/g, '_');
    return {
      rows,
      columns,
      baseName: `${safeType}_${safeSite}`,
    };
  }, [filteredDocuments, isDeliveryChallan, siteName, docType]);

  if (!open) return null;

  const title = `${SITE_DOC_LABELS[docType] || 'Documents'} — ${siteName || 'Site'}`;

  const onPrintDoc = async (doc) => {
    setDownloadingId(doc.id);
    try {
      await downloadSiteDocument(cookies, docType, doc.id, doc.doc_no || doc.label);
    } catch (error) {
      displayError(error?.response?.data ?? error.message, 'Print failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const onOpenLinkedPdf = async (linkedType, linkedId, label) => {
    if (!linkedId) return;
    const busyId = `${linkedType}-${linkedId}`;
    setDownloadingId(busyId);
    try {
      await downloadSiteDocument(cookies, linkedType, linkedId, label);
    } catch (error) {
      displayError(
        error?.response?.data ?? error.message,
        linkedType === 'sales_order' ? 'Sales Order PDF failed' : 'Work Schedule PDF failed',
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const renderLinkedDocLink = (value, linkedType, linkedId, busyKey) => {
    if (!value) return '-';
    if (!linkedId) return value;
    const isBusy = downloadingId === busyKey;
    return (
      <button
        type="button"
        className="btn btn-link btn-sm p-0 site-docs-link-btn"
        disabled={Boolean(downloadingId)}
        onClick={() => onOpenLinkedPdf(linkedType, linkedId, value)}
        title={isBusy ? 'Opening…' : `Open ${value} PDF`}
      >
        {isBusy ? '…' : value}
      </button>
    );
  };

  const runReportExport = (format) => {
    if (!exportPayload.rows.length) {
      displayError('No rows to download.', 'Download failed');
      return;
    }
    const { rows, columns, baseName } = exportPayload;
    try {
      if (format === 'excel') {
        exportAsExcel(columns, rows, baseName);
      } else if (format === 'csv') {
        exportAsCSV(columns, rows, baseName);
      } else {
        exportAsPDF(title, columns, rows, `${baseName}.pdf`);
      }
    } catch (error) {
      displayError(error?.message || error, 'Download failed');
    }
  };

  const renderPrintButton = (doc) => (
    <button
      type="button"
      className="btn btn-sm site-docs-print-btn"
      disabled={downloadingId === doc.id}
      onClick={() => onPrintDoc(doc)}
      title={downloadingId === doc.id ? 'Opening…' : 'Print'}
      aria-label="Print"
    >
      {downloadingId === doc.id ? (
        <span className="site-docs-print-busy">…</span>
      ) : (
        <i className="fa fa-print" aria-hidden="true" />
      )}
    </button>
  );

  return (
    <div className="site-docs-modal-overlay" onClick={onClose} role="presentation">
      <div
        className={`site-docs-modal-panel${isDeliveryChallan ? ' site-docs-modal-panel--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="site-docs-modal-header">
          <h5>{title}</h5>
          <div className="site-docs-modal-header-actions">
            <div className="site-docs-search">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search…"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                aria-label="Search documents"
              />
              <span className="site-docs-search-icon" aria-hidden="true">
                <i className="fa fa-search" />
              </span>
            </div>
            <ReportExportDropdown
              label="Download"
              disabled={loading || filteredDocuments.length === 0}
              onExcel={() => runReportExport('excel')}
              onCSV={() => runReportExport('csv')}
              onPDF={() => runReportExport('pdf')}
            />
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="site-docs-modal-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-4 text-muted">No documents found.</div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-4 text-muted">No matching documents.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered site-docs-modal-table">
                <thead>
                  {isDeliveryChallan ? (
                    <tr>
                      <th>#</th>
                      <th>Project Name</th>
                      <th>SO No.</th>
                      <th>WS No.</th>
                      <th>DC No</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Grade</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                      <th>Vehicle No</th>
                      <th>Driver</th>
                      <th>Total KM</th>
                      <th>Total Time</th>
                      <th>Print</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>#</th>
                      <th>Document No</th>
                      <th>Date</th>
                      <th>Details</th>
                      <th>Action</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {isDeliveryChallan
                    ? filteredDocuments.map((doc, index) => (
                        <tr key={doc.id}>
                          <td>{index + 1}</td>
                          <td>{doc.project_name || '-'}</td>
                          <td>
                            {renderLinkedDocLink(
                              doc.so_no,
                              'sales_order',
                              doc.so_id,
                              `sales_order-${doc.so_id}`,
                            )}
                          </td>
                          <td>
                            {renderLinkedDocLink(
                              doc.ws_no,
                              'schedule',
                              doc.ws_id,
                              `schedule-${doc.ws_id}`,
                            )}
                          </td>
                          <td>{doc.doc_no || doc.label || doc.id}</td>
                          <td>{formatDocDate(doc.doc_date)}</td>
                          <td>{doc.doc_time || '-'}</td>
                          <td>{doc.grade || '-'}</td>
                          <td>{formatNumber(doc.qty, 2)}</td>
                          <td>{formatNumber(doc.rate, 2)}</td>
                          <td>{formatNumber(doc.amount, 2)}</td>
                          <td>{doc.vehicle_no || '-'}</td>
                          <td>{doc.driver || '-'}</td>
                          <td>{formatNumber(doc.total_km, 0)}</td>
                          <td>{formatNumber(doc.total_time, 0)}</td>
                          <td className="text-center">{renderPrintButton(doc)}</td>
                        </tr>
                      ))
                    : filteredDocuments.map((doc, index) => (
                        <tr key={doc.id}>
                          <td>{index + 1}</td>
                          <td>{doc.doc_no || doc.label || doc.id}</td>
                          <td>{formatDocDate(doc.doc_date)}</td>
                          <td>{doc.extra || '-'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={downloadingId === doc.id}
                              onClick={() => onPrintDoc(doc)}
                            >
                              {downloadingId === doc.id ? 'Opening…' : 'Download'}
                            </button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function formatSiteAddress(site = {}) {
  return [site.address_1, site.address_2, site.address_3].filter(Boolean).join(', ');
}

export function formatSiteContact(site = {}) {
  return site.contact_mobile_no || site.mobile_number || site.phone_number || '';
}

export function SiteDocCountCell({ site, docType, onOpen }) {
  const count = site?.document_counts?.[docType];
  const siteId = site?.id;
  if (!siteId) {
    return <span className="text-muted">-</span>;
  }
  const value = Number(count || 0);
  if (value <= 0) {
    return <span className="text-muted">0</span>;
  }
  return (
    <button
      type="button"
      className="site-doc-count-btn"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen(site, docType);
      }}
      title={`View ${SITE_DOC_LABELS[docType]}`}
    >
      {value}
    </button>
  );
}

export default SiteDocumentsModal;
